import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { random } from "@chatbotgang/etude/string/random";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CallIcon from "@mui/icons-material/Call";
import InsertEmoticonOutlinedIcon from "@mui/icons-material/InsertEmoticonOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { type muiTheme, theme } from "@polifonia/theme";
import { reorderList } from "@polifonia/utils/vanilla/reorderList";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import whatsappChatBackgroundUrl from "@/pages/channels/pages/detail/whatsapp/assets/whatsappChatBackground.webp";
import {
  getIceBreakerFieldError,
  ICE_BREAKER_MAX_COUNT,
  ICE_BREAKER_MAX_LENGTH,
  type IceBreakerFieldError,
  isIceBreakersListValid,
} from "@/pages/channels/pages/detail/whatsapp/utils/iceBreakers";

interface IceBreakerRow {
  id: string;
  value: string;
}

function createRow(value = ""): IceBreakerRow {
  return { id: random(), value };
}

const styles = {
  layout: {
    display: "flex",
    alignItems: "flex-start",
    gap: "20px",
    width: "100%",
    "@media (max-width: 900px)": {
      flexWrap: "wrap",
    },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "420px",
    maxWidth: "420px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  rowDragging: {
    opacity: 0.5,
  },
  dragHandle: {
    cursor: "grab",
    color: theme.colors.neutral050,
  },
  actions: {
    display: "flex",
    // Stack defaults to column -- without this, Cancel and Save stack
    // vertically instead of sitting side by side.
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: "8px",
  },
  previewSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    width: "352px",
    minWidth: "352px",
    padding: "12px",
    boxSizing: "border-box",
    borderRadius: "4px",
    bgcolor: "#F0F2F5",
  },
  phone: {
    width: "328px",
    height: "525px",
    overflow: "hidden",
    border: "1px solid #DADDE1",
    borderRadius: "12px",
    bgcolor: theme.colors.colorNeutralWhite,
    boxShadow: "0 1px 4px rgba(11, 20, 26, 0.16)",
  },
  whatsappHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "64px",
    padding: "8px 10px",
    color: theme.colors.colorNeutralWhite,
    bgcolor: "#128C7E",
  },
  headerIcon: {
    fontSize: "20px",
  },
  avatar: {
    width: "36px",
    height: "36px",
    flexShrink: 0,
    borderRadius: "50%",
    bgcolor: "#D9FDD3",
    color: "#128C7E",
    fontWeight: 700,
  },
  contact: {
    minWidth: 0,
    flex: 1,
  },
  contactName: {
    color: theme.colors.colorNeutralWhite,
    fontSize: "0.875rem",
    fontWeight: 600,
  },
  contactStatus: {
    color: theme.colors.colorNeutralWhite,
    fontSize: "0.6875rem",
    opacity: 0.82,
  },
  conversation: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    height: "calc(100% - 64px)",
    padding: "12px 10px 10px",
    backgroundColor: "rgb(241, 233, 222)",
    backgroundImage: `linear-gradient(rgba(241, 233, 222, 0.9), rgba(241, 233, 222, 0.9)), url(${whatsappChatBackgroundUrl})`,
    backgroundPosition: "0% 0%, 0% 0%",
    backgroundRepeat: "repeat, repeat",
    backgroundSize: "cover, cover",
  },
  composer: {
    overflow: "hidden",
    borderRadius: "20px",
    bgcolor: theme.colors.colorNeutralWhite,
    boxShadow: "0 1px 2px rgba(11, 20, 26, 0.16)",
  },
  promptList: {
    display: "flex",
    flexDirection: "column",
    padding: "10px 18px 4px",
  },
  prompt: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "38px",
    borderBottom: "1px solid #D1D7DB",
    color: "#263238",
    fontSize: "0.9375rem",
    overflowWrap: "anywhere",
  },
  promptText: {
    minWidth: 0,
    flex: 1,
  },
  sendIcon: {
    flexShrink: 0,
    color: "#25D366",
    fontSize: "26px",
  },
  messageInput: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    height: "52px",
    padding: "0 14px",
    color: "#AEBAC1",
  },
  inputSpacer: {
    flex: 1,
  },
  inputIcon: {
    fontSize: "24px",
  },
  disclaimer: {
    color: "#8A8D91",
    fontSize: "0.75rem",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface IceBreakersPreviewProps {
  channelName: string;
  phoneNumber: string;
  rows: ReadonlyArray<IceBreakerRow>;
}

const IceBreakersPreview: React.FC<IceBreakersPreviewProps> =
  function IceBreakersPreview({ channelName, phoneNumber, rows }) {
    const { t } = useTranslation();
    const visibleRows = rows.filter((row) => row.value.trim() !== "");

    return (
      <Box sx={styles.previewSection}>
        <Box
          role="region"
          sx={styles.phone}
          aria-label={t(
            "page.channel.whatsapp.automations.iceBreakers.preview.label",
            { defaultValue: "WhatsApp ice breakers preview" },
          )}
        >
          <Box sx={styles.whatsappHeader}>
            <ArrowBackIcon sx={styles.headerIcon} />
            <Avatar sx={styles.avatar}>
              {(channelName.charAt(0) || "?").toUpperCase()}
            </Avatar>
            <Box sx={styles.contact}>
              <Box sx={styles.contactName}>{channelName}</Box>
              <Box sx={styles.contactStatus}>{phoneNumber}</Box>
            </Box>
            <VideocamIcon sx={styles.headerIcon} />
            <CallIcon sx={styles.headerIcon} />
            <MoreVertIcon sx={styles.headerIcon} />
          </Box>
          <Box sx={styles.conversation}>
            <Box sx={styles.composer}>
              <Box sx={styles.promptList}>
                {visibleRows.map((row) => (
                  <Box key={row.id} sx={styles.prompt}>
                    <Box sx={styles.promptText}>{row.value}</Box>
                    <Box sx={styles.sendIcon}>
                      <MotifIcon un-i-motif="paper_plane" />
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box sx={styles.messageInput}>
                <InsertEmoticonOutlinedIcon sx={styles.inputIcon} />
                <Box sx={styles.inputSpacer} />
                <AttachFileIcon sx={styles.inputIcon} />
                <PhotoCameraOutlinedIcon sx={styles.inputIcon} />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={styles.disclaimer}>
          {t(
            "page.channel.whatsapp.automations.iceBreakers.preview.disclaimer",
            {
              defaultValue:
                "This experience may look different across devices.",
            },
          )}
        </Box>
      </Box>
    );
  };

interface IceBreakerRowProps {
  row: IceBreakerRow;
  index: number;
  disabled: boolean;
  errorMessage: string | undefined;
  placeholder: string;
  onChangeValue: (id: string, value: string) => void;
  onBlur: (id: string) => void;
  onRemove: (id: string) => void;
}

/**
 * A single ice breaker field. Split out from `IceBreakersSection` because
 * `useSortable` is a hook -- it has to run once per row, which means once
 * per component instance, not inside the parent's `.map()`.
 */
const IceBreakerRow: React.FC<IceBreakerRowProps> = function IceBreakerRow({
  row,
  index,
  disabled,
  errorMessage,
  placeholder,
  onChangeValue,
  onBlur,
  onRemove,
}) {
  const { t } = useTranslation();
  // `handleRef` (not `ref`) is what makes only the handle button draggable --
  // the row itself (`ref`) stays a plain, non-draggable sortable target, so
  // dragging never fights with selecting text inside the TextField.
  const { ref, handleRef, isDragging } = useSortable({ id: row.id, index });

  const handleChange = useHandler<
    React.ComponentProps<typeof TextField>["onChange"]
  >((e) => {
    onChangeValue(row.id, e.target.value);
  });
  const handleBlur = useHandler(() => {
    onBlur(row.id);
  });
  const handleRemoveClick = useHandler(() => {
    onRemove(row.id);
  });

  return (
    <Box
      ref={ref}
      sx={{ ...styles.row, ...(isDragging && styles.rowDragging) }}
    >
      <IconButton
        ref={handleRef}
        size="small"
        disabled={disabled}
        sx={styles.dragHandle}
        aria-label={t(
          "page.channel.whatsapp.automations.iceBreakers.dragHandle.label",
        )}
      >
        <MotifIcon un-i-motif="drag" />
      </IconButton>
      <TextField
        fullWidth
        size="small"
        value={row.value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        error={!!errorMessage}
        helperText={errorMessage}
        placeholder={placeholder}
        slotProps={{
          htmlInput: { maxLength: ICE_BREAKER_MAX_LENGTH },
          input: {
            endAdornment: (
              <Typography variant="note">
                {row.value.length}/{ICE_BREAKER_MAX_LENGTH}
              </Typography>
            ),
          },
        }}
      />
      <IconButton
        size="small"
        disabled={disabled}
        onClick={handleRemoveClick}
        aria-label={t("common.delete")}
      >
        <MotifIcon un-i-motif="cross" />
      </IconButton>
    </Box>
  );
};

interface IceBreakersSectionProps {
  channelId: number;
  channelName?: string;
  phoneNumber?: string;
  initialPrompts: Array<string>;
}

/**
 * Ice breakers editor for the WhatsApp Automations tab -- mirrors Meta's own
 * "Conversational components" dialog (up to 4 ordered prompts, drag to
 * reorder, per-field 80-char counter) as an AdminCenter-hosted alternative,
 * since Meta's own WhatsApp Manager UI currently fails to save these
 * ("Something went wrong in 3P bot integration", a confirmed Meta-side
 * defect with no fix timeline).
 */
export const IceBreakersSection: React.FC<IceBreakersSectionProps> =
  function IceBreakersSection({
    channelId,
    channelName = "Crescendo Lab",
    phoneNumber = "",
    initialPrompts,
  }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [baseline, setBaseline] = useState<ReadonlyArray<IceBreakerRow>>(() =>
      initialPrompts.map((value) => createRow(value)),
    );
    const [rows, setRows] = useState<ReadonlyArray<IceBreakerRow>>(baseline);
    // A field's "empty" error is only surfaced after the user has blurred it,
    // or attempted to save -- a row the user just added via "Add ice
    // breaker" and hasn't typed into yet shouldn't show as an error on
    // arrival. Other errors (too long, emoji) show immediately: they can
    // only happen after the user has already typed something.
    const [touchedIds, setTouchedIds] = useState<ReadonlySet<string>>(
      () => new Set(),
    );

    const values = useMemo(() => rows.map((row) => row.value), [rows]);
    const isDirty =
      rows.length !== baseline.length ||
      rows.some((row, index) => row.value !== baseline[index]?.value);
    const isValid = isIceBreakersListValid(values);
    const canAddRow = rows.length < ICE_BREAKER_MAX_COUNT;

    const errorMessageByReason: Record<IceBreakerFieldError, string> = useMemo(
      () => ({
        empty: t("page.channel.whatsapp.automations.iceBreakers.error.empty"),
        tooLong: t(
          "page.channel.whatsapp.automations.iceBreakers.error.tooLong",
        ),
        emoji: t("page.channel.whatsapp.automations.iceBreakers.error.emoji"),
      }),
      [t],
    );

    const updateMutation = interlude.channel.useUpdateConversationalComponents(
      {
        params: {
          channelId,
        },
      },
      {
        onSuccess(data) {
          // Re-baseline to the server-confirmed order/values so the form is
          // no longer dirty, mirroring the RHF `.reset(saved)` pattern used
          // elsewhere on this page (e.g. WebStreamConfig) -- just done by
          // hand here since this editor is plain state, not a form library.
          const savedRows = data.prompts.map((value) => createRow(value));
          setBaseline(savedRows);
          setRows(savedRows);
          setTouchedIds(new Set());
          enqueueSnackbar(t("common.updatedSuccessfully"), {
            variant: "success",
          });
        },
      },
    );

    const handleChangeValue = useHandler((id: string, value: string) => {
      setRows((current) =>
        current.map((row) => (row.id === id ? { ...row, value } : row)),
      );
    });

    const handleBlur = useHandler((id: string) => {
      setTouchedIds((current) => new Set(current).add(id));
    });

    const handleAdd = useHandler(() => {
      setRows((current) => [...current, createRow()]);
    });

    const handleRemove = useHandler((id: string) => {
      setRows((current) => current.filter((row) => row.id !== id));
      setTouchedIds((current) => {
        if (!current.has(id)) return current;
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    });

    const handleCancel = useHandler(() => {
      setRows(baseline);
      setTouchedIds(new Set());
    });

    const handleSave = useHandler(() => {
      if (!isValid) {
        // Force every row's error into view, including ones the user never
        // blurred (e.g. added a row then immediately hit Save).
        setTouchedIds(new Set(rows.map((row) => row.id)));
        return;
      }
      updateMutation.mutate({ prompts: values });
    });

    // The reorder seam: kept separate from the dnd-kit event handling below
    // so the ordering logic is swappable/testable independent of whatever
    // control drives it (drag, keyboard, or otherwise) -- `reorderList` is
    // shared with the custom field option editor and tested on its own.
    const moveItem = useHandler((fromIndex: number, toIndex: number) => {
      setRows((current) => reorderList(current, fromIndex, toIndex));
    });

    const handleDragEnd = useHandler<
      React.ComponentProps<typeof DragDropProvider>["onDragEnd"]
    >((event) => {
      if (event.canceled) return;
      const { source } = event.operation;
      if (!isSortable(source)) return;
      const { initialIndex, index } = source;
      if (initialIndex !== index) moveItem(initialIndex, index);
    });

    const isPending = updateMutation.isPending;

    return (
      <Box sx={styles.layout}>
        <Stack sx={styles.section}>
          <Typography
            variant="h3"
            fontWeight="medium"
            color={theme.colors.staticFgTitle}
          >
            {t("page.channel.whatsapp.automations.iceBreakers.title")}
          </Typography>
          <InfoBox
            variant="plain"
            description={t(
              "page.channel.whatsapp.automations.iceBreakers.infoBox.description",
            )}
            href={t(
              "page.channel.whatsapp.automations.iceBreakers.infoBox.link",
            )}
          />
          <DragDropProvider onDragEnd={handleDragEnd}>
            <Stack gap="8px">
              {rows.map((row, index) => {
                const fieldError = getIceBreakerFieldError(row.value);
                const showError =
                  fieldError !== null &&
                  (fieldError !== "empty" || touchedIds.has(row.id));
                return (
                  <IceBreakerRow
                    key={row.id}
                    row={row}
                    index={index}
                    disabled={isPending}
                    errorMessage={
                      showError ? errorMessageByReason[fieldError] : undefined
                    }
                    placeholder={t(
                      "page.channel.whatsapp.automations.iceBreakers.field.placeholder",
                      { index: index + 1 },
                    )}
                    onChangeValue={handleChangeValue}
                    onBlur={handleBlur}
                    onRemove={handleRemove}
                  />
                );
              })}
            </Stack>
          </DragDropProvider>
          <Box>
            <Button
              variant="plain"
              startIcon={<MotifIcon un-i-motif="plus" />}
              onClick={handleAdd}
              disabled={!canAddRow || isPending}
            >
              {t("page.channel.whatsapp.automations.iceBreakers.addButton")}
            </Button>
          </Box>
          <Stack sx={styles.actions}>
            <Button
              variant="plain"
              onClick={handleCancel}
              disabled={!isDirty || isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isPending}
              disabled={!isDirty || isPending}
            >
              {t("common.save")}
            </Button>
          </Stack>
        </Stack>
        <IceBreakersPreview
          channelName={channelName}
          phoneNumber={phoneNumber}
          rows={rows}
        />
      </Box>
    );
  };
